import {
  ElementType,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';

import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import { accent, font, neutral, radius, shadow, text } from 'styles/revampTokens';

import {
  BlockNavigationDropdownItem,
  BlockNavigationClickOptions,
} from './BlockNavigationDropdown';
import DashboardNavigationPackRow from './DashboardNavigationPackRow';
import DashboardNavigationPreviewPane from './DashboardNavigationPreviewPane';
import { AVATAR_TINTS, DashboardNavPack, hashIndex } from './dashboardNavDropdownShared';

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const PlusCircleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <circle cx='8' cy='8' r='6.5' stroke='currentColor' strokeWidth='1.2' />
    <path d='M8 5v6M5 8h6' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' />
  </svg>
);

interface DashboardNavigationDropdownProperties {
  dashboards: DashboardNavPack[];
  activeDashboardId?: string | null;
  currentPageId?: string | number;
  allInstruments?: any[];
  onDashboardOpen: (dashboardId: string) => void;
  onFetchPackPages?: (dashboardId: string) => Promise<BlockNavigationDropdownItem[]>;
  onPageClick: (page: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
  onCreateDashboard?: () => void;
  onDuplicateDashboard?: (dashboardId: string) => void;
  onDeleteDashboard?: (dashboardId: string) => void;
  onRenameDashboard?: (dashboardId: string, name: string) => void;
  canDeleteDashboard?: boolean;
}

const DashboardNavigationDropdown = ({
  dashboards,
  activeDashboardId,
  currentPageId,
  allInstruments,
  onDashboardOpen,
  onFetchPackPages,
  onPageClick,
  onCreateDashboard,
  onDuplicateDashboard,
  onDeleteDashboard,
  onRenameDashboard,
  canDeleteDashboard = true,
}: DashboardNavigationDropdownProperties): ReactElement => {
  const [query, setQuery] = useState('');
  const [hoverPageId, setHoverPageId] = useState<string | number | null>(currentPageId ?? null);
  const [expandedPackIds, setExpandedPackIds] = useState<Set<string>>(() => new Set());
  const [pagesByPackId, setPagesByPackId] = useState<Record<string, BlockNavigationDropdownItem[]>>(
    {},
  );
  const [loadingPackIds, setLoadingPackIds] = useState<Set<string>>(() => new Set());

  const needle = query.trim().toLowerCase();

  useEffect(() => {
    if (!activeDashboardId) return;
    setExpandedPackIds((previous) => new Set(previous).add(activeDashboardId));
  }, [activeDashboardId]);

  useEffect(() => {
    setPagesByPackId((previous) => {
      const next = { ...previous };
      dashboards.forEach((pack) => {
        if (pack.pages.length > 0) {
          next[pack.id] = pack.pages;
        }
      });
      return next;
    });
  }, [dashboards]);

  const resolvePackPages = useCallback(
    (pack: DashboardNavPack) => pagesByPackId[pack.id] ?? pack.pages,
    [pagesByPackId],
  );

  const togglePackExpanded = useCallback(
    async (pack: DashboardNavPack, event: ReactMouseEvent) => {
      event.stopPropagation();
      const isExpanded = expandedPackIds.has(pack.id);
      if (isExpanded) {
        setExpandedPackIds((previous) => {
          const next = new Set(previous);
          next.delete(pack.id);
          return next;
        });
        return;
      }

      setExpandedPackIds((previous) => new Set(previous).add(pack.id));

      const knownPages = resolvePackPages(pack);
      if (knownPages.length > 0 || !onFetchPackPages) {
        return;
      }

      setLoadingPackIds((previous) => new Set(previous).add(pack.id));
      try {
        const fetched = await onFetchPackPages(pack.id);
        setPagesByPackId((previous) => ({ ...previous, [pack.id]: fetched }));
      } finally {
        setLoadingPackIds((previous) => {
          const next = new Set(previous);
          next.delete(pack.id);
          return next;
        });
      }
    },
    [expandedPackIds, onFetchPackPages, resolvePackPages],
  );

  const filteredDashboards = useMemo(() => {
    if (!needle) return dashboards;
    return dashboards
      .map((pack) => {
        const packMatches = pack.title.toLowerCase().includes(needle);
        const pages = resolvePackPages(pack).filter((page) =>
          page.title.toLowerCase().includes(needle),
        );
        if (packMatches) return { ...pack, pages: resolvePackPages(pack) };
        if (pages.length > 0) return { ...pack, pages };
        return null;
      })
      .filter(Boolean) as DashboardNavPack[];
  }, [dashboards, needle, resolvePackPages]);

  useEffect(() => {
    if (!needle) return;
    setExpandedPackIds((previous) => {
      const next = new Set(previous);
      filteredDashboards.forEach((pack) => {
        if (pack.pages.length > 0) {
          next.add(pack.id);
        }
      });
      return next;
    });
  }, [filteredDashboards, needle]);

  const activePack = useMemo(
    () => filteredDashboards.find((pack) => pack.id === activeDashboardId) ?? filteredDashboards[0],
    [activeDashboardId, filteredDashboards],
  );

  const previewPage = useMemo(() => {
    if (hoverPageId != null) {
      const hovered = filteredDashboards
        .flatMap((pack) => resolvePackPages(pack))
        .find((page) => page.id === hoverPageId);
      if (hovered) return hovered;
    }
    const activePages = activePack ? resolvePackPages(activePack) : [];
    return activePages.find((p) => p.id === currentPageId) ?? activePages[0] ?? null;
  }, [activePack, currentPageId, filteredDashboards, hoverPageId, resolvePackPages]);

  const previewTint = useMemo(() => {
    if (previewPage) {
      return AVATAR_TINTS[hashIndex(previewPage.title, AVATAR_TINTS.length)];
    }
    if (activePack) {
      return AVATAR_TINTS[hashIndex(activePack.title, AVATAR_TINTS.length)];
    }
    return AVATAR_TINTS[0];
  }, [activePack, previewPage]);

  const packActions = useMemo((): ReactNode => {
    if (!activePack) return null;
    const pack = activePack;
    return (
      <DropdownFlex gap='8px' flexWrap='wrap'>
        {onRenameDashboard && (
          <DropdownFlex
            as='button'
            type='button'
            px='10px'
            py='6px'
            borderRadius={radius.control}
            border={`1px solid ${neutral.border}`}
            bg={neutral.surface}
            fontFamily={font.body}
            fontSize='12px'
            fontWeight='600'
            color={text.primary}
            cursor='pointer'
            _hover={{ bg: neutral.surfaceHover }}
            onClick={() => {
              const next = window.prompt('Dashboard name', pack.title);
              if (next && next.trim() && next.trim() !== pack.title) {
                onRenameDashboard(pack.id, next.trim());
              }
            }}
          >
            Rename
          </DropdownFlex>
        )}
        {onDuplicateDashboard && (
          <DropdownFlex
            as='button'
            type='button'
            px='10px'
            py='6px'
            borderRadius={radius.control}
            border={`1px solid ${neutral.border}`}
            bg={neutral.surface}
            fontFamily={font.body}
            fontSize='12px'
            fontWeight='600'
            color={text.primary}
            cursor='pointer'
            _hover={{ bg: neutral.surfaceHover }}
            onClick={() => onDuplicateDashboard(pack.id)}
          >
            Duplicate
          </DropdownFlex>
        )}
        {onDeleteDashboard && canDeleteDashboard && (
          <DropdownFlex
            as='button'
            type='button'
            px='10px'
            py='6px'
            borderRadius={radius.control}
            border={`1px solid ${neutral.border}`}
            bg={neutral.surface}
            fontFamily={font.body}
            fontSize='12px'
            fontWeight='600'
            color='#C53030'
            cursor='pointer'
            _hover={{ bg: '#FFF5F5' }}
            onClick={() => {
              const confirmed = window.confirm(
                `Delete "${pack.title}"? This removes all pages and widgets in this dashboard.`,
              );
              if (confirmed) {
                onDeleteDashboard(pack.id);
              }
            }}
          >
            Delete
          </DropdownFlex>
        )}
      </DropdownFlex>
    );
  }, [activePack, canDeleteDashboard, onDeleteDashboard, onDuplicateDashboard, onRenameDashboard]);

  const handlePageHover = useCallback((pageId: string | number) => {
    setHoverPageId(pageId);
  }, []);

  return (
    <DropdownFlex
      w='720px'
      maxW='96vw'
      h='auto'
      maxH='calc(100vh - 80px)'
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      boxShadow={shadow.overlay}
      borderRadius={radius.overlay}
      overflow='hidden'
      flexDirection='column'
      onClick={(event: ReactMouseEvent) => event.stopPropagation()}
    >
      <DropdownFlex
        alignItems='center'
        gap='8px'
        px='14px'
        h='46px'
        flexShrink={0}
        borderBottom={`1px solid ${neutral.border}`}
      >
        <FigmaSearchIcon width={13} height={13} color={text.muted} />
        <DropdownBox
          as='input'
          type='text'
          value={query}
          placeholder='Search dashboards and pages...'
          onChange={(event: any) => setQuery(event.target.value)}
          flex='1'
          minW={0}
          bg='transparent'
          border='none'
          outline='none'
          color={text.primary}
          fontFamily={font.body}
          fontSize='13px'
          sx={{ '::placeholder': { color: text.muted } }}
        />
      </DropdownFlex>

      <DropdownFlex minH='280px' maxH='calc(100vh - 180px)'>
        <DropdownFlex direction='column' w='360px' flexShrink={0} overflowY='auto' p='8px'>
          {filteredDashboards.length === 0 ? (
            <DropdownBox
              px='12px'
              py='32px'
              textAlign='center'
              color={text.secondary}
              fontSize='13px'
              fontFamily={font.body}
            >
              {needle ? 'No dashboards match your search' : 'No dashboards yet'}
            </DropdownBox>
          ) : (
            <>
              <DropdownBox
                px='12px'
                pt='6px'
                pb='5px'
                color={text.muted}
                fontFamily={font.body}
                fontSize='10.5px'
                fontWeight='700'
                letterSpacing='0.07em'
                textTransform='uppercase'
              >
                Dashboards
              </DropdownBox>
              {filteredDashboards.map((pack) => {
                const packPages = resolvePackPages(pack);
                return (
                  <DashboardNavigationPackRow
                    key={pack.id}
                    pack={pack}
                    packPages={packPages}
                    isActivePack={pack.id === activeDashboardId}
                    isExpanded={expandedPackIds.has(pack.id)}
                    isLoadingPages={loadingPackIds.has(pack.id)}
                    previewPageId={previewPage?.id}
                    onToggleExpand={togglePackExpanded}
                    onDashboardOpen={onDashboardOpen}
                    onPageHover={handlePageHover}
                    onPageClick={onPageClick}
                  />
                );
              })}
            </>
          )}
        </DropdownFlex>

        <DropdownFlex
          direction='column'
          flex='1 1 0'
          minW={0}
          borderLeft={`1px solid ${neutral.border}`}
          bg={neutral.surfaceMuted}
        >
          <DashboardNavigationPreviewPane
            previewPage={previewPage}
            activePack={activePack}
            previewTint={previewTint}
            packActions={packActions}
            allInstruments={allInstruments}
            onPageClick={onPageClick}
          />
        </DropdownFlex>
      </DropdownFlex>

      <DropdownFlex
        px='16px'
        h='46px'
        flexShrink={0}
        alignItems='center'
        justifyContent='flex-start'
        borderTop={`1px solid ${neutral.border}`}
        bg={neutral.surfaceMuted}
      >
        <DropdownFlex
          as='button'
          type='button'
          alignItems='center'
          gap='7px'
          color={accent.solid}
          fontFamily={font.body}
          fontSize='12.5px'
          fontWeight='600'
          cursor='pointer'
          _hover={{ opacity: 0.8 }}
          onClick={onCreateDashboard}
        >
          <PlusCircleIcon />
          Create dashboard
        </DropdownFlex>
      </DropdownFlex>
    </DropdownFlex>
  );
};

export default DashboardNavigationDropdown;

export { type DashboardNavPack } from './dashboardNavDropdownShared';
